import React from "react";
import { StagePosition } from "./offeringCatalog";

/**
 * Presentation tokens for the theme-aware prototype match (ADO 9290).
 *
 * Accent hues stay constant across light and dark surfaces. Where an accent
 * would fail contrast as text on a light surface, the light-mode value is a
 * slightly darker shade of the same hue and the true accent is restored via a
 * `dark:` variant. All class strings are literal so Tailwind's JIT can compile
 * the arbitrary values; do not build these dynamically.
 */

/** "+" new marker: faint accent tint, accent border, accent text. */
export const stageMarkerClasses: Record<StagePosition, string> = {
  1: "bg-[#34D399]/15 border-[#34D399]/50 text-[#0e9f6e] dark:text-[#34D399]",
  2: "bg-[#60A5FA]/15 border-[#60A5FA]/50 text-[#2563EB] dark:text-[#60A5FA]",
  3: "bg-[#A78BFA]/15 border-[#A78BFA]/50 text-[#7C3AED] dark:text-[#A78BFA]",
  4: "bg-[#FBBF24]/20 border-[#FBBF24]/60 text-[#B45309] dark:text-[#FBBF24]",
};

/** Solid accent chip used for the stage number circle. */
export const stageSolidBgClasses: Record<StagePosition, string> = {
  1: "bg-[#34D399]",
  2: "bg-[#60A5FA]",
  3: "bg-[#A78BFA]",
  4: "bg-[#FBBF24]",
};

/** Inline gradient bar for a category header, keyed off its accent hex. */
export function categoryGradientStyle(hex: string): React.CSSProperties {
  return {
    backgroundImage: `linear-gradient(90deg, ${hex} 12%, transparent 70%)`,
  };
}

/** Subtle blue wash marking the member's current stage column, both themes. */
export const CURRENT_COLUMN_TINT =
  "bg-[#2f5fc0]/[0.05] dark:bg-[#2f5fc0]/[0.15]";

/**
 * CURRENT_COLUMN_TINT pre-composited over the grid surface, for the sticky
 * header cells: a translucent wash there would let scrolled rows show through.
 * Light is the tint over white, dark is the tint over slate-800.
 *
 * Only the sticky header uses this. To roll the sticky header back, restore
 * CURRENT_COLUMN_TINT in StageHeaderCell and delete this token.
 */
export const CURRENT_COLUMN_TINT_OPAQUE = "bg-[#F5F7FC] dark:bg-[#21314F]";

/**
 * The matrix header row (ADO 9570): sticky positioning plus the rule that
 * separates it from the offering rows scrolling underneath.
 *
 * `top-12` clears MainPageWrapper's own sticky page header (48px, z-[5]); the
 * z-index sits below both that and its stickyContent slot (z-[4]) so the page
 * chrome always wins. Sticky only takes effect above the matrix wrapper's
 * `min-[1340px]` breakpoint, where the wrapper stops being a scroll container;
 * the bottom rule applies at every width.
 *
 * Roll the sticky header back by dropping `sticky top-12 z-[3]` from this
 * token and reverting the wrapper's `min-[1340px]:overflow-x-visible`.
 */
export const MATRIX_HEADER_CELL =
  "sticky top-12 z-[3] border-b border-gray-200 dark:border-slate-700";

/** "YOU ARE HERE" pill: solid blue, white text. */
export const YOU_ARE_HERE_PILL = "bg-[#2f5fc0] text-white";

/** A la carte accent text (orange), darkened for light surfaces. */
export const ALC_ACCENT_TEXT = "text-[#EA580C] dark:text-[#FF6B1A]";

/** A la carte availability dot: orange with a soft glow. */
export const ALC_DOT = "bg-[#FF6B1A] shadow-[0_0_8px_rgba(255,107,26,0.5)]";
